import React, { useCallback, useEffect, useState } from "react";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSelector } from "react-redux";

import FirebaseAuthHelper from "../../../authentication/helpers/firebase-auth-helper/firebase-auth-helper";
import AuthHelper from "../../../authentication/helpers/auth-helper/auth-helper";
import ContactApi from "../../../../api/contact-api";
import ContactHelper from "../../../../entities/contact/contact-helper";
import ThreadApi from "../../../../api/thread-api";
import router from "../../../../shared/top-level-declarations/history";
import "./embed-connect-contacts.css";
import OutsideClickHandler from "react-outside-click-handler";
import { Tooltip } from "react-tooltip";

const STORAGE_KEY = "embed-connect-form";

const validateMobileNumber = (mobile) => {
  let value = mobile?.trim() || "";
  value = value.replace(/[^+\d]/g, "");
  if (value === "") return { isValid: false, error: "Phone number is required." };
  if (!value.startsWith("+")) {
    return { isValid: false, error: "Phone number must start with a country code (e.g., +1)." };
  }
  const digits = value.replace("+", "");
  if (digits.length < 10) return { isValid: false, error: "At least 10 digits required." };
  if (digits.length > 15) return { isValid: false, error: "Must not exceed 15 digits." };
  return { isValid: true, formattedNumber: value };
};

const validateEmail = (email) => {
  const value = (email || "").trim();
  if (value === "") {
    return { isValid: false, error: "Email is required." };
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return { isValid: false, error: "Enter a valid email address." };
  }
  return { isValid: true, formatted: value };
};

const getFirstName = (fullName) => (fullName || "").split(" ")[0] || "";

const EmbedConnectContactsPage = () => {
  const { userInfo } = useSelector((s) => s.userStore);
  const [contact1Name, setContact1Name] = useState("");
  const [contact1Mobile, setContact1Mobile] = useState("");
  const [contact1Email, setContact1Email] = useState("");
  const [contact2Name, setContact2Name] = useState("");
  const [contact2Mobile, setContact2Mobile] = useState("");
  const [contact2Email, setContact2Email] = useState("");
  const [selectedMedium1, setSelectedMedium1] = useState("whatsapp");
  const [selectedMedium2, setSelectedMedium2] = useState("whatsapp");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [manualOpenUrl, setManualOpenUrl] = useState("");

  // Ensure firebase is initialized
  useEffect(() => {
    FirebaseAuthHelper.initFirebase();
  }, []);

  const getCurrentUser = useCallback(() => FirebaseAuthHelper.getCurrentUser(), []);

  // Load stored form if present and user already logged in
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setContact1Name(parsed.contact1Name || "");
        setContact1Mobile(parsed.contact1Mobile || "");
        setContact1Email(parsed.contact1Email || "");
        setContact2Name(parsed.contact2Name || "");
        setContact2Mobile(parsed.contact2Mobile || "");
        setContact2Email(parsed.contact2Email || "");
        setSelectedMedium1(parsed.selectedMedium1 || "whatsapp");
        setSelectedMedium2(parsed.selectedMedium2 || "whatsapp");
      } catch {}
    }
  }, []);

  const processContact = async (name, phone, email, preferredPlatform) => {
    try {
      const payload = {};
      if (phone) payload.phone = phone;
      if (email) payload.email = email;
      const { data: checkResult } = await ContactApi.private.checkIfContactExists(payload);
      if (checkResult?.data?._id) return checkResult.data._id;
      throw new Error("Contact check returned an invalid response.");
    } catch (err) {
      if (err?.response?.status === 404) {
        const { contactId } = await ContactHelper.createNewContact({ 
          name, 
          phone, 
          email, 
          preferredPlatform 
        });
        if (contactId) return contactId;
        throw new Error("Failed to create new contact.");
      }
      throw err;
    }
  };

  const submitForm = useCallback(async (newTabWindow = null) => {
    // Validate contact 1
    if (!contact1Name) {
      setError("Contact One: Name is required");
      return false;
    }
    
    let contact1Data = { name: contact1Name };
    if (selectedMedium1 === "whatsapp") {
      const v1 = validateMobileNumber(contact1Mobile);
      if (!v1.isValid) {
        setError(`Contact One: ${v1.error}`);
        return false;
      }
      contact1Data.phone = v1.formattedNumber;
    } else if (selectedMedium1 === "email") {
      const e1 = validateEmail(contact1Email);
      if (!e1.isValid) {
        setError(`Contact One: ${e1.error}`);
        return false;
      }
      contact1Data.email = e1.formatted;
    }

    // Validate contact 2
    if (!contact2Name) {
      setError("Contact Two: Name is required");
      return false;
    }
    
    let contact2Data = { name: contact2Name };
    if (selectedMedium2 === "whatsapp") {
      const v2 = validateMobileNumber(contact2Mobile);
      if (!v2.isValid) {
        setError(`Contact Two: ${v2.error}`);
        return false;
      }
      contact2Data.phone = v2.formattedNumber;
    } else if (selectedMedium2 === "email") {
      const e2 = validateEmail(contact2Email);
      if (!e2.isValid) {
        setError(`Contact Two: ${e2.error}`);
        return false;
      }
      contact2Data.email = e2.formatted;
    }

    setError("");
    setIsProcessing(true);
    try {
      const contact1Id = await processContact(contact1Data.name, contact1Data.phone, contact1Data.email, selectedMedium1);
      const contact2Id = await processContact(contact2Data.name, contact2Data.phone, contact2Data.email, selectedMedium2);
      const roomTitle = `${getFirstName(contact1Name)}+${getFirstName(contact2Name)}`;
      await ThreadApi.private.createThread({
        type: "room",
        title: roomTitle,
        _contacts: [contact1Id, contact2Id],
        _users: [],
        communicationMetadata: [
          {
            _contact: contact1Id,
            preferredPlatform: selectedMedium1,
          },
          {
            _contact: contact2Id,
            preferredPlatform: selectedMedium2,
          },
        ],
      });
      // Clear stored form and reset UI fields
      sessionStorage.removeItem(STORAGE_KEY);
      try {
        setContact1Name("");
        setContact1Mobile("");
        setContact1Email("");
        setContact2Name("");
        setContact2Mobile("");
        setContact2Email("");
        setSelectedMedium1("whatsapp");
        setSelectedMedium2("whatsapp");
        setError("");
      } catch (_) {
        // ignore state update issues
      }
      // Decide navigation target
      const path = "/connect-contacts";
      const absoluteUrl = new URL(path, window.location.origin).toString();

      // If we have a user-gesture pre-opened tab, use it
      if (newTabWindow && !newTabWindow.closed) {
        try {
          newTabWindow.location = absoluteUrl;
          return true; // New tab navigation handled
        } catch (_) {
          // If assigning fails, fall back to parent message below
        }
      }

      // Otherwise, request parent to handle redirect (still opens outside iframe if parent listens)
      try {
        const ref = document.referrer || "";
        const parentOrigin = ref ? new URL(ref).origin : "*";
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: "EMBED_REDIRECT", source: "relai-embed", url: absoluteUrl },
            parentOrigin
          );
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        } else {
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        }
      } catch (_) {
        // Show manual open option if anything fails
        setManualOpenUrl(absoluteUrl);
      }
      return false; // Did not use new tab navigation
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Unknown error occurred";
      setError(message);
    } finally {
      setIsProcessing(false);
    }
  }, [contact1Mobile, contact1Name, contact1Email, contact2Mobile, contact2Name, contact2Email, selectedMedium1, selectedMedium2]);

  const handleConnect = async () => {
    setManualOpenUrl("");
    const user = getCurrentUser();
    // Persist current form
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ 
        contact1Name, 
        contact1Mobile, 
        contact1Email, 
        contact2Name, 
        contact2Mobile, 
        contact2Email,
        selectedMedium1,
        selectedMedium2
      })
    );

    // Basic synchronous validation before opening a new tab
    if (!contact1Name) {
      setError("Contact One: Name is required");
      return;
    }
    if (selectedMedium1 === "whatsapp") {
      const v1 = validateMobileNumber(contact1Mobile);
      if (!v1.isValid) {
        setError(`Contact One: ${v1.error}`);
        return;
      }
    } else if (selectedMedium1 === "email") {
      const e1 = validateEmail(contact1Email);
      if (!e1.isValid) {
        setError(`Contact One: ${e1.error}`);
        return;
      }
    }
    
    if (!contact2Name) {
      setError("Contact Two: Name is required");
      return;
    }
    if (selectedMedium2 === "whatsapp") {
      const v2 = validateMobileNumber(contact2Mobile);
      if (!v2.isValid) {
        setError(`Contact Two: ${v2.error}`);
        return;
      }
    } else if (selectedMedium2 === "email") {
      const e2 = validateEmail(contact2Email);
      if (!e2.isValid) {
        setError(`Contact Two: ${e2.error}`);
        return;
      }
    }

    if (user) {
      // User already logged in: pre-open new tab synchronously
      let newTab = null;
      try { newTab = window.open("", "_blank"); } catch (_) {}
      const usedNewTab = await submitForm(newTab);
      // If we pre-opened a tab but didn't use it, close it
      if (!usedNewTab && newTab && !newTab.closed) {
        try { newTab.close(); } catch (_) {}
      }
      return;
    }

    try {
      // Not logged in: trigger Firebase Google login (popup) and backend exchange
      await AuthHelper.loginB2CWithGoogle({ skipNavigate: true });
      // After login completes, attempt to open a new tab and navigate it
      let postLoginTab = null;
      try { postLoginTab = window.open("", "_blank"); } catch (_) {}
      const usedNewTab = await submitForm(postLoginTab);
      if (!usedNewTab && postLoginTab && !postLoginTab.closed) {
        try { postLoginTab.close(); } catch (_) {}
      }
    } catch (e) {
      // On any error, nothing to close here unless a tab was opened post-login
      if (e?.code === "auth/popup-blocked") {
        // Offer a manual new-tab route to login outside the iframe
        const loginUrl = new URL("/login-b2c", window.location.origin).toString();
        setManualOpenUrl(loginUrl);
        setError("Popup was blocked by the browser.");
      } else if (e?.code === "auth/popup-closed-by-user") {
        setError("Login was cancelled. Please try again if you want to continue.");
      } else if (e?.code === "auth/cancelled-popup-request") {
        setError("Multiple login attempts detected. Please try again.");
      } else {
        setError(e?.message || "Login failed. Please try again.");
      }
    }

  };

  const handleOpenConnectContactsLink = useCallback(async (e) => {
    e?.preventDefault?.();
    setError("");
    setManualOpenUrl("");

    const targetPath = "/connect-contacts";
    const absoluteUrl = new URL(targetPath, window.location.origin).toString();

    // Pre-open a blank tab synchronously
    let newTab = null;
    try {
      newTab = window.open("", "_blank");
    } catch (_) {}

    const user = getCurrentUser();
    if (user) {
      // If a new tab is available, use it; else fallback to parent/in-iframe
      if (newTab && !newTab.closed) {
        try {
          newTab.location = absoluteUrl;
          return;
        } catch (_) {}
      }
      try {
        const ref = document.referrer || "";
        const parentOrigin = ref ? new URL(ref).origin : "*";
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: "EMBED_REDIRECT", source: "relai-embed", url: absoluteUrl },
            parentOrigin
          );
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        } else {
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        }
      } catch (_) {
        // Show manual open option if anything fails
        setManualOpenUrl(absoluteUrl);
      }
      return;
    }

    // Not logged in: do login then navigate
    try {
      await AuthHelper.loginB2CWithGoogle({ skipNavigate: true });
      if (newTab && !newTab.closed) {
        try {
          newTab.location = absoluteUrl;
          return;
        } catch (_) {}
      }
      try {
        const ref = document.referrer || "";
        const parentOrigin = ref ? new URL(ref).origin : "*";
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: "EMBED_REDIRECT", source: "relai-embed", url: absoluteUrl },
            parentOrigin
          );
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        } else {
          // Show manual open option instead of navigating inside iframe
          setManualOpenUrl(absoluteUrl);
        }
      } catch (_) {
        // Show manual open option if anything fails
        setManualOpenUrl(absoluteUrl);
      }
    } catch (e) {
      if (newTab && !newTab.closed) {
        try { newTab.close(); } catch (_) {}
      }
      if (e?.code === "auth/popup-blocked") {
        const loginUrl = new URL("/login-b2c", window.location.origin).toString();
        setManualOpenUrl(loginUrl);
        setError("Popup was blocked by the browser.");
      } else if (e?.code === "auth/popup-closed-by-user") {
        setError("Login was cancelled. Please try again if you want to continue.");
      } else if (e?.code === "auth/cancelled-popup-request") {
        setError("Multiple login attempts detected. Please try again.");
      } else {
        setError(e?.message || "Login failed. Please try again.");
      }
    }
  }, [getCurrentUser]);



  const SelectApp = ({ value, onChange }) => {
    const [openSelectPopup, setOpenSelectPopup] = useState(false);

    const apps = [
      {
        id: "whatsapp",
        label: "WhatsApp",
        icon: (
          <svg viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>Whatsapp-color</title> <desc>Created with Sketch.</desc> <defs> </defs> <g id="Icons" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#67C15E"> <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"> </path> </g> </g> </g></svg>
        ),
      },
      {
        id: "email",
        label: "Email",
        icon: (
          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"> <mask id="mask0_2035_3687" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="17"> <path d="M16 0.958984H0V16.959H16V0.958984Z" fill="white"/> </mask> <g mask="url(#mask0_2035_3687)"> <path d="M15.9992 6.28605V6.64565C15.9805 6.659 15.9564 6.6749 15.9323 6.6909L15.3349 7.0984C15.2947 7.12505 15.2465 7.157 15.1983 7.19165C14.9491 7.35675 14.625 7.57785 14.4107 7.71905L9.16812 11.2643L9.00202 11.3761L8.51442 11.7064C8.45282 11.7463 8.38052 11.7783 8.30012 11.7996C8.26797 11.8103 8.23047 11.8156 8.19567 11.8209C8.19027 11.8236 8.18492 11.8236 8.18227 11.8236C8.12067 11.8316 8.05907 11.8316 8.00012 11.8289C7.94117 11.8316 7.87957 11.8316 7.81792 11.8236C7.77777 11.8182 7.73757 11.8102 7.70007 11.7996C7.61967 11.7783 7.54737 11.7463 7.48577 11.7064L7.26612 11.5572L6.99822 11.3761L1.58954 7.71905C1.5306 7.6791 1.46363 7.6338 1.38863 7.5859C1.20113 7.4607 0.981447 7.31155 0.801947 7.1917C0.753727 7.15705 0.705507 7.1251 0.665322 7.09845L0.0679451 6.6909C0.0438516 6.67495 0.0197266 6.659 0.000976562 6.64565V6.28605C0.000976562 6.2248 0.00900756 6.1769 0.0224141 6.1316C0.0224141 6.129 0.0251016 6.129 0.0251016 6.129C0.0385076 6.1183 0.0518826 6.1103 0.0679451 6.09965L0.260822 5.96645L0.665322 5.69211C0.919787 5.52167 1.33504 5.23933 1.58951 5.07151L7.48577 1.08417C7.62777 0.993609 7.81527 0.950984 8.00012 0.961641C8.01887 0.961641 8.03492 0.958984 8.05367 0.958984H8.05637C8.22247 0.958984 8.38857 1.00164 8.51447 1.0842L14.4107 5.07151C14.6223 5.21001 14.9438 5.42842 15.1929 5.59626C15.2438 5.63086 15.292 5.66286 15.3349 5.69211L15.7394 5.96645L15.9323 6.09965C15.9483 6.1103 15.9617 6.1183 15.9751 6.129C15.9912 6.1742 15.9992 6.2248 15.9992 6.28605Z" fill="url(#paint0_linear_2035_3687)"/> <path d="M16 7.88049V8.23804C15.9804 8.25189 15.9553 8.26854 15.933 8.28514L15.3337 8.69259C15.0801 8.86164 14.6647 9.14429 14.4111 9.31339L8.51564 13.2988C8.45154 13.3403 8.37904 13.3708 8.30104 13.393C8.20629 13.4207 8.10034 13.429 7.99999 13.4235C7.89964 13.429 7.79374 13.4207 7.69899 13.393C7.62089 13.3708 7.54844 13.3403 7.48434 13.2988L1.58887 9.31334C1.33522 9.14424 0.919874 8.86159 0.666249 8.69254L0.0669065 8.28514C0.0446565 8.26849 0.0195315 8.25189 0 8.23804V7.88049C0 7.81954 0.00837499 7.76964 0.0223125 7.72534C0.0223125 7.72254 0.025094 7.72254 0.025094 7.72254C0.0390315 7.71144 0.052969 7.70309 0.066875 7.69199L0.262029 7.55899L0.666219 7.28459C0.919844 7.11554 1.33519 6.83289 1.58884 6.66379L7.48429 2.67841C7.62649 2.58694 7.81599 2.54535 7.99999 2.55644C8.18394 2.54535 8.37629 2.58691 8.51564 2.67841L14.4111 6.66379C14.6647 6.83289 15.0801 7.11554 15.3337 7.28459L15.7379 7.55899L15.933 7.69199C15.947 7.70309 15.9609 7.71139 15.9748 7.72254C15.9916 7.76684 16 7.81954 16 7.88049Z" fill="url(#paint1_linear_2035_3687)"/> <path d="M8.19567 10.7875V11.9115H8.16352L7.80452 11.9354H0.000976562V6.38995C0.000976562 6.3287 0.00900756 6.27805 0.0224141 6.23545C0.0224141 6.2328 0.0251016 6.2328 0.0251016 6.2328C0.0545701 6.13955 0.113508 6.0863 0.191195 6.073L0.526072 6.27005L0.684102 6.3633L1.40473 6.7868L1.44223 6.8081L1.90032 7.07715L7.11342 10.1508L7.12412 10.1562L8.15817 10.7635L8.19567 10.7875Z" fill="#CC8529"/> <path d="M8.33492 11.4667C8.33492 11.4694 8.28137 11.5067 8.19567 11.568C8.14207 11.6026 8.07512 11.6479 8.00012 11.7012C7.96262 11.7251 7.92242 11.7517 7.87957 11.7811L7.81792 11.8237L7.70007 11.9036L7.64917 11.9355L6.81602 12.4975C6.72762 12.5561 6.63922 12.6174 6.55887 12.6733C6.40617 12.7772 6.28027 12.8624 6.23742 12.8891C6.17577 12.9343 5.91862 13.1075 5.66412 13.2806L4.2711 14.2208L4.26573 14.2262L4.26307 14.2288L0.844822 16.5355C0.785882 16.5755 0.721602 16.6181 0.659977 16.6607C0.625132 16.6847 0.590322 16.706 0.555507 16.7299H0.266197C0.110821 16.7299 0.0224141 16.6953 0.00366408 16.6367C0.00366408 16.6341 0.00366408 16.6341 0.00366408 16.6341C0.00366408 16.6234 0.000976576 16.6154 0.00366408 16.6021C0.000976576 16.5861 0.000976562 16.5701 0.000976562 16.5515V6.39004C0.000976562 6.32879 0.00900756 6.27819 0.0224141 6.23554C0.0224141 6.23289 0.0251016 6.23289 0.0251016 6.23289C0.0545701 6.13969 0.113508 6.08639 0.191195 6.07309C0.215289 6.06509 0.236727 6.06509 0.260852 6.06774C0.319787 6.07039 0.386757 6.09704 0.459102 6.14499L1.40741 6.78424L1.44223 6.80819L1.8521 7.08519C1.86013 7.09054 1.86816 7.09584 1.8762 7.10119C2.12801 7.27169 2.52448 7.54069 2.77095 7.70584L7.56612 10.9473C7.57152 10.95 7.57687 10.9527 7.57952 10.9553C7.58757 10.9607 7.59562 10.966 7.60362 10.9713C7.66792 11.0139 7.72952 11.0566 7.78577 11.0938C7.87957 11.1551 7.95457 11.2084 8.00012 11.2403C8.02687 11.2563 8.04567 11.267 8.04832 11.2696C8.05902 11.2776 8.12602 11.3229 8.19567 11.3735C8.19837 11.3735 8.19837 11.3762 8.20102 11.3762C8.27337 11.4268 8.33492 11.4641 8.33492 11.4667Z" fill="#F6B75A"/> <path d="M15.9964 6.3332C15.9987 6.36445 16.0006 6.42445 16.0006 6.46655V16.4738C16.0006 16.5159 16.0006 16.5584 16.0006 16.5683C16.0006 16.5782 15.9978 16.6073 15.9978 16.6127C15.9978 16.618 15.9978 16.6224 15.9978 16.6335V16.6349C15.9978 16.6349 15.9733 16.6575 15.9434 16.6833C15.9135 16.7092 15.7013 16.7304 15.6592 16.7304H15.5224C15.4803 16.7304 15.4172 16.7113 15.3822 16.688L15.2196 16.5789C15.1846 16.5556 15.1274 16.5172 15.0925 16.4936L10.3998 13.3228C10.365 13.2992 10.3079 13.2607 10.273 13.2371L9.82542 12.9323C9.79067 12.9086 9.73367 12.8699 9.69882 12.8463L9.24847 12.5415C9.21367 12.5178 9.15667 12.4792 9.12172 12.4556L8.36497 11.9453C8.33007 11.9218 8.27297 11.8833 8.23807 11.8598L8.18387 11.8233C8.14897 11.7998 8.09167 11.7616 8.05662 11.7383C8.05662 11.7383 8.03677 11.7252 8.00052 11.7002C7.80817 11.5672 7.66602 11.4702 7.66602 11.4674C7.66602 11.4646 7.72737 11.4259 7.79982 11.376C7.87227 11.3261 7.88997 11.3139 7.88997 11.3139C7.92472 11.2901 7.95752 11.2675 7.96287 11.2637C7.96822 11.2599 8.02877 11.2205 8.06332 11.1964C8.06332 11.1964 8.22347 11.085 8.39632 10.9713C8.40472 10.9657 8.40472 10.9657 8.40472 10.9657C8.41392 10.9597 8.42332 10.9534 8.42557 10.9519C8.42787 10.9504 8.45542 10.9326 8.47997 10.9159C8.50447 10.8991 8.55312 10.8661 8.58797 10.8426L13.1664 7.7493C13.2012 7.72575 13.2583 7.68716 13.2931 7.6635L14.0862 7.1284C14.1211 7.10491 14.1782 7.0664 14.213 7.0428L15.4771 6.18895C15.512 6.1654 15.5709 6.1298 15.608 6.1099C15.608 6.1099 15.6412 6.09 15.6757 6.0824C15.7102 6.07475 15.772 6.0765 15.8129 6.08625L15.9314 6.1694C15.9557 6.2039 15.9941 6.30195 15.9964 6.3332Z" fill="#F0A642"/> <path d="M15.9997 15.292V16.5503C15.9997 16.5697 15.9997 16.5864 15.997 16.603C15.9997 16.6141 15.997 16.6224 15.997 16.6335C15.997 16.6335 15.997 16.6335 15.997 16.6363C15.9774 16.6945 15.8882 16.7305 15.7349 16.7305H1.44922L4.26175 14.2278L4.26453 14.225L7.78505 11.0933L8.12795 10.7912L8.21985 10.708L8.5237 10.8854L15.9997 15.292Z" fill="#CC8529"/> <path d="M16 16.5033V16.5504C16 16.5698 16 16.5864 15.9972 16.603C16 16.6141 15.9972 16.6225 15.9972 16.6335C15.9972 16.6335 15.9972 16.6335 15.9972 16.6363C15.9776 16.6945 15.8885 16.7305 15.7352 16.7305H0.26481C0.111531 16.7305 0.0223125 16.6945 0.00278151 16.6363C0.00278151 16.6335 0.00278151 16.6335 0.00278151 16.6335C0.00278151 16.6225 1.02445e-08 16.6141 0.00278151 16.603C1.02445e-08 16.5864 0 16.5698 0 16.5504V16.5033C0.0250935 16.47 0.061344 16.4368 0.108719 16.4035L7.5484 10.9769C7.5512 10.9769 7.5512 10.9741 7.554 10.9714C7.5624 10.9658 7.57075 10.9603 7.5791 10.9548C7.7324 10.8466 7.8885 10.7718 8 10.7441C8.03905 10.7552 8.0808 10.769 8.12825 10.7912C8.21745 10.8273 8.32055 10.8855 8.4209 10.9548C8.43205 10.963 8.4404 10.9686 8.45155 10.9769L15.8912 16.4035C15.9386 16.4395 15.9749 16.4728 16 16.5033Z" fill="#F6AF47"/> </g> <defs> <linearGradient id="paint0_linear_2035_3687" x1="8.00012" y1="11.8305" x2="8.00012" y2="0.958953" gradientUnits="userSpaceOnUse"> <stop stop-color="#DA9621"/> <stop offset="0.7685" stop-color="#EAA12F"/> </linearGradient> <linearGradient id="paint1_linear_2035_3687" x1="7.99999" y1="13.4251" x2="7.99999" y2="2.5547" gradientUnits="userSpaceOnUse"> <stop stop-color="#DA9621"/> <stop offset="0.7685" stop-color="#EAA12F"/> </linearGradient> </defs> </svg>
        ),
      },
    ];

    const selectedApp = apps.find((a) => a.id === value) || apps[0];

    return (
      <div className="selectAppWrapper">
        <div className="selectedApp" onClick={(e) => setOpenSelectPopup(true)}>
          <div className="appIcon">
            {selectedApp.icon}
            <div className="downArrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 10L12 15L17 10" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            </div>
          </div>
        </div>

        <OutsideClickHandler onOutsideClick={(e) => setOpenSelectPopup(false)}>
          <div className={"selectAppPopup " + (openSelectPopup ? "show" : "")}>
            {apps.map((app) => (
              <div key={app.id} className={"app " + (value === app.id ? "active" : "")} onClick={() => { onChange?.(app.id); setOpenSelectPopup(false); }}>
                <div className="appIcon">{app.icon}</div>
                <div className="appLabel">{app.label}</div>
              </div>
            ))}
            <div className="app disabled">
              <div className="appIcon"><svg xmlns="http://www.w3.org/2000/svg" aria-label="WeChat" role="img" viewBox="0 0 512 512" fill="#fff" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><rect width="512" height="512" rx="15%" fill="#00c70a"></rect><path d="M402 369c23-17 38 -42 38 -70c0-51 -50 -92 -111 -92s-110 41-110 92s49 92 110 92c13 0 25-2 36 -5c4-1 8 0 9 1l25 14c3 2 6 0 5-4l-6-22c0-3 2 -5 4 -6m-110-85a15 15 0 110-29a15 15 0 010 29m74 0a15 15 0 110-29a15 15 0 010 29"></path><path d="m205 105c-73 0-132 50-132 111 0 33 17 63 45 83 3 2 5 5 4 10l-7 24c-1 5 3 7 6 6l30-17c3-2 7-3 11-2 26 8 48 6 51 6-24-84 59-132 123-128-10-52-65-93-131-93m-44 93a18 18 0 1 1 0-35 18 18 0 0 1 0 35m89 0a18 18 0 1 1 0-35 18 18 0 0 1 0 35"></path></g></svg></div>
              <div className="appLabel">WeChat</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"> <g clip-path="url(#clip0_2035_3699)"> <path fill-rule="evenodd" clip-rule="evenodd" d="M0.242188 0.958984V14.959L2.95734 12.159H15.7573V0.958984H0.242188Z" fill="#4D79FF"/> <path fill-rule="evenodd" clip-rule="evenodd" d="M2.56934 4.15894H13.042V5.75894H2.56934V4.15894ZM2.56934 7.35894H8.77537V8.95894H2.56934V7.35894Z" fill="white"/> </g> <defs> <clipPath id="clip0_2035_3699"> <rect width="16" height="14" fill="white" transform="translate(0 0.958984)"/> </clipPath> </defs> </svg> </div>
              <div className="appLabel">SMS</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 48 48"> <path fill="#0f0" d="M35,6H13c-3.866,0-7,3.134-7,7v22c0,3.866,3.134,7,7,7h22c3.866,0,7-3.134,7-7V13	C42,9.134,38.866,6,35,6z"></path><path fill="#fff" d="M24,12c-7.18,0-13,4.925-13,11c0,4.074,2.624,7.622,6.513,9.523C16.931,33.773,16.087,34.949,15,36	c2.162-0.38,4.191-1.175,5.937-2.32C21.921,33.882,22.943,34,24,34c7.18,0,13-4.925,13-11S31.18,12,24,12z"></path> </svg></div>
              <div className="appLabel">iMessage</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.5 7.91058C9.5 7.66118 9.7022 7.45898 9.9516 7.45898H15.5484C15.7978 7.45898 16 7.66118 16 7.91058V11.209C16 13.0039 14.545 14.459 12.75 14.459C10.955 14.459 9.5 13.0039 9.5 11.209V7.91058Z" fill="url(#paint0_linear_2035_3664)"/> <path d="M4.5 7.07186C4.5 6.73338 4.7744 6.45898 5.11288 6.45898H11.8871C12.2256 6.45898 12.5 6.73338 12.5 7.07186V11.959C12.5 14.1681 10.7092 15.959 8.5 15.959C6.29084 15.959 4.5 14.1681 4.5 11.959V7.07186Z" fill="url(#paint1_linear_2035_3664)"/> <path d="M13.5 6.45898C14.3284 6.45898 15 5.78742 15 4.95898C15 4.13056 14.3284 3.45898 13.5 3.45898C12.6716 3.45898 12 4.13056 12 4.95898C12 5.78742 12.6716 6.45898 13.5 6.45898Z" fill="#34439E"/> <path d="M13.5 6.45898C14.3284 6.45898 15 5.78742 15 4.95898C15 4.13056 14.3284 3.45898 13.5 3.45898C12.6716 3.45898 12 4.13056 12 4.95898C12 5.78742 12.6716 6.45898 13.5 6.45898Z" fill="url(#paint2_linear_2035_3664)"/> <path d="M9 5.95898C10.1046 5.95898 11 5.06354 11 3.95898C11 2.85442 10.1046 1.95898 9 1.95898C7.89544 1.95898 7 2.85442 7 3.95898C7 5.06354 7.89544 5.95898 9 5.95898Z" fill="url(#paint3_linear_2035_3664)"/> <mask id="mask0_2035_3664" style={{"mask-type":"luminance"}} maskUnits="userSpaceOnUse" x="4" y="0" width="9" height="16"> <path d="M8.5 5.95898C9.88076 5.95898 11 4.83972 11 3.45898C11 2.07825 9.88076 0.958984 8.5 0.958984C7.11924 0.958984 6 2.07825 6 3.45898C6 4.83972 7.11924 5.95898 8.5 5.95898Z" fill="url(#paint4_linear_2035_3664)"/> <path d="M5.11288 6.45898C4.7744 6.45898 4.5 6.73338 4.5 7.07186V11.959C4.5 14.1681 6.29084 15.959 8.5 15.959C10.7092 15.959 12.5 14.1681 12.5 11.959V7.07186C12.5 6.73338 12.2256 6.45898 11.8871 6.45898H5.11288Z" fill="url(#paint5_linear_2035_3664)"/> </mask> <g mask="url(#mask0_2035_3664)"> <path d="M3.5 6.95898C3.5 6.13054 4.17156 5.45898 5 5.45898H8.5C9.32844 5.45898 10 6.13054 10 6.95898V12.959C10 13.7874 9.32844 14.459 8.5 14.459H3.5V6.95898Z" fill="black" fill-opacity="0.3"/> </g> <path d="M8 4.45898H1C0.447716 4.45898 0 4.9067 0 5.45898V12.459C0 13.0113 0.447716 13.459 1 13.459H8C8.55228 13.459 9 13.0113 9 12.459V5.45898C9 4.9067 8.55228 4.45898 8 4.45898Z" fill="url(#paint6_linear_2035_3664)"/> <path d="M6.5 6.45898H2.5V7.37634H3.99747V11.459H5.00256V7.37634H6.5V6.45898Z" fill="white"/> <defs> <linearGradient id="paint0_linear_2035_3664" x1="9.5" y1="7.82738" x2="16.0796" y2="12.1267" gradientUnits="userSpaceOnUse"> <stop stop-color="#364088"/> <stop offset="1" stop-color="#6E7EE1"/> </linearGradient> <linearGradient id="paint1_linear_2035_3664" x1="4.5" y1="10.6609" x2="12.5" y2="10.6609" gradientUnits="userSpaceOnUse"> <stop stop-color="#515FC4"/> <stop offset="1" stop-color="#7084EA"/> </linearGradient> <linearGradient id="paint2_linear_2035_3664" x1="12" y1="3.61688" x2="14.8982" y2="5.65634" gradientUnits="userSpaceOnUse"> <stop stop-color="#364088"/> <stop offset="1" stop-color="#6E7EE1"/> </linearGradient> <linearGradient id="paint3_linear_2035_3664" x1="7.57144" y1="2.53042" x2="10.1428" y2="5.53042" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint4_linear_2035_3664" x1="6.71428" y1="1.67327" x2="9.92856" y2="5.42326" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint5_linear_2035_3664" x1="6.71428" y1="1.67327" x2="9.92856" y2="5.42326" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint6_linear_2035_3664" x1="-2.6077e-08" y1="8.95898" x2="9" y2="8.95898" gradientUnits="userSpaceOnUse"> <stop stop-color="#2A3887"/> <stop offset="1" stop-color="#4C56B9"/> </linearGradient> </defs> </svg> </div>
              <div className="appLabel">Teams</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M14.4615 8.34335C15.3111 8.34335 15.9999 7.65461 15.9999 6.80501C15.9999 5.95541 15.311 5.26668 14.4614 5.26668C13.6118 5.26668 12.9229 5.95548 12.9229 6.80514V8.34335H14.4615ZM10.1537 8.34335C11.0034 8.34335 11.6922 7.65455 11.6922 6.80489V2.49745C11.6922 1.64778 11.0034 0.958984 10.1537 0.958984C9.30403 0.958984 8.61523 1.64778 8.61523 2.49745V6.80489C8.61523 7.65455 9.30403 8.34335 10.1537 8.34335Z" fill="#2EB67D"/> <path d="M1.53833 9.57458C0.688732 9.57458 0 10.2633 0 11.1129C0 11.9625 0.6888 12.6513 1.53839 12.6513C2.38806 12.6513 3.07692 11.9625 3.07692 11.1128V9.57458H1.53833ZM5.84615 9.57458C4.99649 9.57458 4.30769 10.2634 4.30769 11.113V15.4205C4.30769 16.2702 4.99649 16.959 5.84615 16.959C6.69582 16.959 7.38462 16.2702 7.38462 15.4205V11.113C7.38462 10.2634 6.69582 9.57458 5.84615 9.57458Z" fill="#E01E5A"/> <path d="M8.61523 15.4206C8.61523 16.2702 9.30397 16.959 10.1536 16.959C11.0032 16.959 11.6919 16.2702 11.6919 15.4206C11.6919 14.5709 11.0031 13.882 10.1534 13.882H8.61523V15.4206ZM8.61523 11.1128C8.61523 11.9625 9.30403 12.6513 10.1537 12.6513H14.4611C15.3108 12.6513 15.9996 11.9625 15.9996 11.1128C15.9996 10.2631 15.3108 9.57434 14.4611 9.57434H10.1537C9.30403 9.57434 8.61523 10.2631 8.61523 11.1128Z" fill="#ECB22E"/> <path d="M7.38437 2.49732C7.38437 1.64772 6.69563 0.958984 5.84603 0.958984C4.99643 0.958984 4.30769 1.64778 4.30769 2.49738C4.30769 3.34705 4.99649 4.03591 5.84615 4.03591H7.38437V2.49732ZM7.38437 6.80514C7.38437 5.95548 6.69557 5.26668 5.84591 5.26668H1.53846C0.688794 5.26668 0 5.95548 0 6.80514C0 7.6548 0.688794 8.3436 1.53846 8.3436H5.84591C6.69557 8.3436 7.38437 7.6548 7.38437 6.80514Z" fill="#36C5F0"/> </svg> </div>
              <div className="appLabel">Slack</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg xmlns="http://www.w3.org/2000/svg" aria-label="WeChat" role="img" viewBox="0 0 512 512" fill="#fff" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><rect width="512" height="512" rx="15%" fill="#00c70a"></rect><path d="M402 369c23-17 38 -42 38 -70c0-51 -50 -92 -111 -92s-110 41-110 92s49 92 110 92c13 0 25-2 36 -5c4-1 8 0 9 1l25 14c3 2 6 0 5-4l-6-22c0-3 2 -5 4 -6m-110-85a15 15 0 110-29a15 15 0 010 29m74 0a15 15 0 110-29a15 15 0 010 29"></path><path d="m205 105c-73 0-132 50-132 111 0 33 17 63 45 83 3 2 5 5 4 10l-7 24c-1 5 3 7 6 6l30-17c3-2 7-3 11-2 26 8 48 6 51 6-24-84 59-132 123-128-10-52-65-93-131-93m-44 93a18 18 0 1 1 0-35 18 18 0 0 1 0 35m89 0a18 18 0 1 1 0-35 18 18 0 0 1 0 35"></path></g></svg></div>
              <div className="appLabel">WeChat</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"> <g clip-path="url(#clip0_2035_3699)"> <path fill-rule="evenodd" clip-rule="evenodd" d="M0.242188 0.958984V14.959L2.95734 12.159H15.7573V0.958984H0.242188Z" fill="#4D79FF"/> <path fill-rule="evenodd" clip-rule="evenodd" d="M2.56934 4.15894H13.042V5.75894H2.56934V4.15894ZM2.56934 7.35894H8.77537V8.95894H2.56934V7.35894Z" fill="white"/> </g> <defs> <clipPath id="clip0_2035_3699"> <rect width="16" height="14" fill="white" transform="translate(0 0.958984)"/> </clipPath> </defs> </svg> </div>
              <div className="appLabel">SMS</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 48 48"> <path fill="#0f0" d="M35,6H13c-3.866,0-7,3.134-7,7v22c0,3.866,3.134,7,7,7h22c3.866,0,7-3.134,7-7V13	C42,9.134,38.866,6,35,6z"></path><path fill="#fff" d="M24,12c-7.18,0-13,4.925-13,11c0,4.074,2.624,7.622,6.513,9.523C16.931,33.773,16.087,34.949,15,36	c2.162-0.38,4.191-1.175,5.937-2.32C21.921,33.882,22.943,34,24,34c7.18,0,13-4.925,13-11S31.18,12,24,12z"></path> </svg></div>
              <div className="appLabel">iMessage</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.5 7.91058C9.5 7.66118 9.7022 7.45898 9.9516 7.45898H15.5484C15.7978 7.45898 16 7.66118 16 7.91058V11.209C16 13.0039 14.545 14.459 12.75 14.459C10.955 14.459 9.5 13.0039 9.5 11.209V7.91058Z" fill="url(#paint0_linear_2035_3664)"/> <path d="M4.5 7.07186C4.5 6.73338 4.7744 6.45898 5.11288 6.45898H11.8871C12.2256 6.45898 12.5 6.73338 12.5 7.07186V11.959C12.5 14.1681 10.7092 15.959 8.5 15.959C6.29084 15.959 4.5 14.1681 4.5 11.959V7.07186Z" fill="url(#paint1_linear_2035_3664)"/> <path d="M13.5 6.45898C14.3284 6.45898 15 5.78742 15 4.95898C15 4.13056 14.3284 3.45898 13.5 3.45898C12.6716 3.45898 12 4.13056 12 4.95898C12 5.78742 12.6716 6.45898 13.5 6.45898Z" fill="#34439E"/> <path d="M13.5 6.45898C14.3284 6.45898 15 5.78742 15 4.95898C15 4.13056 14.3284 3.45898 13.5 3.45898C12.6716 3.45898 12 4.13056 12 4.95898C12 5.78742 12.6716 6.45898 13.5 6.45898Z" fill="url(#paint2_linear_2035_3664)"/> <path d="M9 5.95898C10.1046 5.95898 11 5.06354 11 3.95898C11 2.85442 10.1046 1.95898 9 1.95898C7.89544 1.95898 7 2.85442 7 3.95898C7 5.06354 7.89544 5.95898 9 5.95898Z" fill="url(#paint3_linear_2035_3664)"/> <mask id="mask0_2035_3664" style={{"mask-type":"luminance"}} maskUnits="userSpaceOnUse" x="4" y="0" width="9" height="16"> <path d="M8.5 5.95898C9.88076 5.95898 11 4.83972 11 3.45898C11 2.07825 9.88076 0.958984 8.5 0.958984C7.11924 0.958984 6 2.07825 6 3.45898C6 4.83972 7.11924 5.95898 8.5 5.95898Z" fill="url(#paint4_linear_2035_3664)"/> <path d="M5.11288 6.45898C4.7744 6.45898 4.5 6.73338 4.5 7.07186V11.959C4.5 14.1681 6.29084 15.959 8.5 15.959C10.7092 15.959 12.5 14.1681 12.5 11.959V7.07186C12.5 6.73338 12.2256 6.45898 11.8871 6.45898H5.11288Z" fill="url(#paint5_linear_2035_3664)"/> </mask> <g mask="url(#mask0_2035_3664)"> <path d="M3.5 6.95898C3.5 6.13054 4.17156 5.45898 5 5.45898H8.5C9.32844 5.45898 10 6.13054 10 6.95898V12.959C10 13.7874 9.32844 14.459 8.5 14.459H3.5V6.95898Z" fill="black" fill-opacity="0.3"/> </g> <path d="M8 4.45898H1C0.447716 4.45898 0 4.9067 0 5.45898V12.459C0 13.0113 0.447716 13.459 1 13.459H8C8.55228 13.459 9 13.0113 9 12.459V5.45898C9 4.9067 8.55228 4.45898 8 4.45898Z" fill="url(#paint6_linear_2035_3664)"/> <path d="M6.5 6.45898H2.5V7.37634H3.99747V11.459H5.00256V7.37634H6.5V6.45898Z" fill="white"/> <defs> <linearGradient id="paint0_linear_2035_3664" x1="9.5" y1="7.82738" x2="16.0796" y2="12.1267" gradientUnits="userSpaceOnUse"> <stop stop-color="#364088"/> <stop offset="1" stop-color="#6E7EE1"/> </linearGradient> <linearGradient id="paint1_linear_2035_3664" x1="4.5" y1="10.6609" x2="12.5" y2="10.6609" gradientUnits="userSpaceOnUse"> <stop stop-color="#515FC4"/> <stop offset="1" stop-color="#7084EA"/> </linearGradient> <linearGradient id="paint2_linear_2035_3664" x1="12" y1="3.61688" x2="14.8982" y2="5.65634" gradientUnits="userSpaceOnUse"> <stop stop-color="#364088"/> <stop offset="1" stop-color="#6E7EE1"/> </linearGradient> <linearGradient id="paint3_linear_2035_3664" x1="7.57144" y1="2.53042" x2="10.1428" y2="5.53042" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint4_linear_2035_3664" x1="6.71428" y1="1.67327" x2="9.92856" y2="5.42326" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint5_linear_2035_3664" x1="6.71428" y1="1.67327" x2="9.92856" y2="5.42326" gradientUnits="userSpaceOnUse"> <stop stop-color="#4858AE"/> <stop offset="1" stop-color="#4E60CE"/> </linearGradient> <linearGradient id="paint6_linear_2035_3664" x1="-2.6077e-08" y1="8.95898" x2="9" y2="8.95898" gradientUnits="userSpaceOnUse"> <stop stop-color="#2A3887"/> <stop offset="1" stop-color="#4C56B9"/> </linearGradient> </defs> </svg> </div>
              <div className="appLabel">Teams</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
            <div className="app disabled">
              <div className="appIcon"><svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M14.4615 8.34335C15.3111 8.34335 15.9999 7.65461 15.9999 6.80501C15.9999 5.95541 15.311 5.26668 14.4614 5.26668C13.6118 5.26668 12.9229 5.95548 12.9229 6.80514V8.34335H14.4615ZM10.1537 8.34335C11.0034 8.34335 11.6922 7.65455 11.6922 6.80489V2.49745C11.6922 1.64778 11.0034 0.958984 10.1537 0.958984C9.30403 0.958984 8.61523 1.64778 8.61523 2.49745V6.80489C8.61523 7.65455 9.30403 8.34335 10.1537 8.34335Z" fill="#2EB67D"/> <path d="M1.53833 9.57458C0.688732 9.57458 0 10.2633 0 11.1129C0 11.9625 0.6888 12.6513 1.53839 12.6513C2.38806 12.6513 3.07692 11.9625 3.07692 11.1128V9.57458H1.53833ZM5.84615 9.57458C4.99649 9.57458 4.30769 10.2634 4.30769 11.113V15.4205C4.30769 16.2702 4.99649 16.959 5.84615 16.959C6.69582 16.959 7.38462 16.2702 7.38462 15.4205V11.113C7.38462 10.2634 6.69582 9.57458 5.84615 9.57458Z" fill="#E01E5A"/> <path d="M8.61523 15.4206C8.61523 16.2702 9.30397 16.959 10.1536 16.959C11.0032 16.959 11.6919 16.2702 11.6919 15.4206C11.6919 14.5709 11.0031 13.882 10.1534 13.882H8.61523V15.4206ZM8.61523 11.1128C8.61523 11.9625 9.30403 12.6513 10.1537 12.6513H14.4611C15.3108 12.6513 15.9996 11.9625 15.9996 11.1128C15.9996 10.2631 15.3108 9.57434 14.4611 9.57434H10.1537C9.30403 9.57434 8.61523 10.2631 8.61523 11.1128Z" fill="#ECB22E"/> <path d="M7.38437 2.49732C7.38437 1.64772 6.69563 0.958984 5.84603 0.958984C4.99643 0.958984 4.30769 1.64778 4.30769 2.49738C4.30769 3.34705 4.99649 4.03591 5.84615 4.03591H7.38437V2.49732ZM7.38437 6.80514C7.38437 5.95548 6.69557 5.26668 5.84591 5.26668H1.53846C0.688794 5.26668 0 5.95548 0 6.80514C0 7.6548 0.688794 8.3436 1.53846 8.3436H5.84591C6.69557 8.3436 7.38437 7.6548 7.38437 6.80514Z" fill="#36C5F0"/> </svg> </div>
              <div className="appLabel">Slack</div>
              <span className="badge rounded-pill">coming soon</span>
            </div>
          </div>
        </OutsideClickHandler>
      </div>
    )
  }

  return (
    <div className="embed-widget">
      <div className="widget-card">
        <div className="widget-header">
          <div className="widget-title">
            <span className="label">✨ Try Jely in ten seconds ✨</span>
            
            <div className="hoverInfo">
              <span data-tooltip-id="aboutJelyMagic"><svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g fill="#000000"> <path d="M8 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 018 7zM8 4a1 1 0 000 2h.007a1 1 0 000-2H8z"></path> <path fill-rule="evenodd" d="M0 8a8 8 0 1116 0A8 8 0 010 8zm8-6.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z" clip-rule="evenodd"></path> </g> </g></svg></span>

              <Tooltip id="aboutJelyMagic" place="left">
                Jely's features are magic. And by that, we mean that we can connect two people up anonymously (and keep their details hidden from each other). Try it out below - and watch the whole chat unfold!
              </Tooltip>
            </div>
          </div>
          {/* <div className="widget-subtitle">No downloads, no logins, no faff. Just pure Jely magic.</div> */}
        </div>

        <div className="widget-body">
          <div className="widget-field">
            <label>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
              <span>Contact One</span>
            </label>
            <input
              type="text"
              className="widget-input"
              placeholder="Full name"
              value={contact1Name}
              onChange={(e) => setContact1Name(e.target.value)}
            />
            <div className="widget-phone">
              <SelectApp value={selectedMedium1} onChange={setSelectedMedium1} />

              {selectedMedium1 === "whatsapp" ? (
                <PhoneInputWithCountrySelect
                  placeholder="Phone number"
                  defaultCountry="GB"
                  value={contact1Mobile}
                  onChange={setContact1Mobile}
                  international={true}
                  withCountryCallingCode={true}
                  countryCallingCodeEditable={false}
                  countrySelectProps={{ unicodeFlags: true }}
                />
              ) : (
                <input
                  type="email"
                  className="widget-input"
                  placeholder="Email address"
                  value={contact1Email}
                  onChange={(e) => setContact1Email(e.target.value)}
                />
              )}
            </div>
          </div>

          <div className="icon">
            <svg width="64" height="41" viewBox="0 0 64 41" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.9 6.9V1.7C17.9 1.3 17.6 1 17.2 1L1.7 11C1.3 11.3 1 12.1 1 12.4C1 12.4 1.3 13.6 1.7 13.9L17.2 24C17.6 24 17.9 23.7 17.9 23.3V18" stroke="black"></path><path d="M17.095 7H34V18H17" stroke="black"></path><path d="M46 22.8V17.6C46 17.2 46.3 16.9 46.7 16.9L62.2 26.9C62.6 27.2 62.9 28 62.9 28.3C62.9 28.3 62.6 29.5 62.2 29.8L46.7 40C46.3 40 46 39.7 46 39.3V34" stroke="black"></path><path d="M46.9056 23H30V34H47" stroke="black"></path></svg>
          </div>

          <div className="widget-field">
            <label>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
              <span>Contact Two</span>
            </label>

            
            <input
              type="text"
              className="widget-input"
              placeholder="Full name"
              value={contact2Name}
              onChange={(e) => setContact2Name(e.target.value)}
            />
            
            <div className="widget-phone">
              <SelectApp value={selectedMedium2} onChange={setSelectedMedium2} />

              {selectedMedium2 === "whatsapp" ? (
                <PhoneInputWithCountrySelect
                  placeholder="Phone number"
                  defaultCountry="GB"
                  value={contact2Mobile}
                  onChange={setContact2Mobile}
                  international={true}
                  withCountryCallingCode={true}
                  countryCallingCodeEditable={false}
                  countrySelectProps={{ unicodeFlags: true }}
                />
              ) : (
                <input
                  type="email"
                  className="widget-input"
                  placeholder="Email address"
                  value={contact2Email}
                  onChange={(e) => setContact2Email(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        {error && <div className="widget-error">{error}</div>}

        {/* manual open overlay is rendered outside the card */}

        <div className="action">
          <button
            className={`widget-button bouncy-button ${isProcessing ? "disabled" : ""}`}
            onClick={!isProcessing ? handleConnect : undefined}
          >
            <span>{isProcessing ? "Connecting..." : "Connect"}</span>
          </button>
          
          <div className="extraLink">
            <a href="#" onClick={handleOpenConnectContactsLink}>Click here</a> to see previous conversations you've made
          </div>

          <div className="footNote">* Jely relays all messages to ensure user contact details remain private.</div>
        </div>

      </div>
      {manualOpenUrl && (
        <div className="embed-overlay">
          <div className="overlay-content">
            <div className="overlay-text">
              Pop-up was blocked. Click below to open your dashboard.
            </div>
            <a
              className="bouncy-button big"
              href={manualOpenUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Open Dashboard</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedConnectContactsPage;
